// ---------- la quête : ce qui est ouvert, ce qui est passé, ce qui vient ----------
//
// La règle tient en une phrase : une partie à la fois, dans l'ordre ; bloqué, on « passe », un
// nombre de fois limité par chapitre (règle `passesPerChapter`), et la partie passée attend avec
// un drapeau. Le chapitre suivant s'ouvre quand `unlockAt` parties du précédent sont finies.
// Les deux nombres viennent du serveur (`GET /content`, réglés dans l'atelier).
//
// Une passe se RENDS quand on finit la partie passée : c'est le coup de pouce qu'on voulait, pas
// une pénalité qui s'accumule. Tout ici se déduit du contenu et de src/store.ts, sans état propre.

import { isDone, skippedIn } from './store';
import type { ChapterSummary, Content, PuzzleSummary } from '../shared/types';

export type NodeState = 'done' | 'skipped' | 'current' | 'locked';

/** Parties finies d'un chapitre. */
export const doneCount = (ch: ChapterSummary): number =>
  ch.puzzles.filter((p) => isDone(p.id)).length;

/** Ce qu'il faut pour ouvrir la suite, borné au nombre de parties : un chapitre de six parties
 *  ne demande pas huit. */
const needOf = (c: Content, ch: ChapterSummary): number =>
  Math.min(c.rules.unlockAt, ch.puzzles.length);

/** Un chapitre est ouvert si c'est le premier, ou si le précédent a assez de parties finies. */
export function isOpen(c: Content, index: number): boolean {
  if (index <= 0) return true;
  const prev = c.chapters[index - 1];
  return isOpen(c, index - 1) && doneCount(prev) >= needOf(c, prev);
}

/** Les passes en cours : les parties passées et pas encore finies. */
const pending = (ch: ChapterSummary): string[] =>
  skippedIn(ch.id).filter((id) => !isDone(id) && ch.puzzles.some((p) => p.id === id));

export const passesLeft = (c: Content, ch: ChapterSummary): number =>
  Math.max(0, c.rules.passesPerChapter - pending(ch).length);

/** La prochaine partie à jouer : la première ni finie ni passée. */
export function currentOf(ch: ChapterSummary): PuzzleSummary | undefined {
  const skipped = new Set(skippedIn(ch.id));
  return ch.puzzles.find((p) => !isDone(p.id) && !skipped.has(p.id));
}

export function stateOf(c: Content, index: number, p: PuzzleSummary): NodeState {
  const ch = c.chapters[index];
  if (isDone(p.id)) return 'done';
  if (!isOpen(c, index)) return 'locked';
  if (skippedIn(ch.id).includes(p.id)) return 'skipped';
  return currentOf(ch)?.id === p.id ? 'current' : 'locked';
}

/** Ce qui reste avant d'ouvrir le chapitre suivant (0 : il est ouvert). */
export const toUnlock = (c: Content, index: number): number =>
  Math.max(0, needOf(c, c.chapters[index]) - doneCount(c.chapters[index]));

/** Le chapitre où le joueur en est : le dernier ouvert qui n'est pas entièrement fini, sinon le
 *  dernier ouvert. C'est celui que l'accueil montre d'abord. */
export function frontier(c: Content): number {
  let last = 0;
  for (let i = 0; i < c.chapters.length && isOpen(c, i); i++) {
    last = i;
    if (doneCount(c.chapters[i]) < c.chapters[i].puzzles.length) return i;
  }
  return last;
}

/** Après une partie finie : la suivante à proposer — la prochaine du chapitre, une passée qui
 *  attend, ou la première du chapitre suivant s'il vient de s'ouvrir. */
export function nextAfter(
  c: Content,
  index: number,
): { index: number; puzzle: PuzzleSummary } | null {
  const ch = c.chapters[index];
  const cur = currentOf(ch);
  if (cur) return { index, puzzle: cur };
  const waiting = pending(ch)[0];
  if (waiting) return { index, puzzle: ch.puzzles.find((p) => p.id === waiting)! };
  const n = index + 1;
  if (n < c.chapters.length && isOpen(c, n)) {
    const first = currentOf(c.chapters[n]);
    if (first) return { index: n, puzzle: first };
  }
  return null;
}
